// AdminWsListener.jsx
import { useEffect, useRef } from "react";
import { notification } from "antd";
import { API_BASE_URL, WS_ENDPOINT_URL } from "configs/AppConfig";
import { ensureWS, subscribe } from "./wsClient";
import { useAdminNotifications } from "contexts/AdminNotificationsContext";
import { playNotificationSound } from "utils/notificationSound";

export default function AdminWsListener() {
  const { addFromWs } = useAdminNotifications();
  const tokenPollRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    function getToken() {
      const raw = localStorage.getItem("auth_token");
      if (!raw) return null;
      return raw.replace(/^Bearer\s+/i, "").replace(/^"|"$/g, "");
    }

    function getWsUrl() {
      const base = (WS_ENDPOINT_URL || API_BASE_URL || "").replace(/\/api\/?$/, "");
      return WS_ENDPOINT_URL
        ? WS_ENDPOINT_URL
        : base.replace(/^http/, "ws") + "/ws/";
    }

    function handleMessage(ev) {
      // ev.data is a JSON string from server
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      if (msg?.type === "pong") return;

      const { event, payload, type, action, data } = msg || {};

      // Handle SAB real-time updates
      if (type && ["dashboard", "events", "mainboard", "scrollboard"].includes(type)) {
        // These are handled by individual components via their own WebSocket listeners
        // Show notification for significant updates
        if (action === "create" || action === "update") {
          notification.info({
            message: `${type.charAt(0).toUpperCase() + type.slice(1)} Updated`,
            description: `${type.charAt(0).toUpperCase() + type.slice(1)} has been ${action}d`,
            duration: 3,
            placement: 'topRight',
          });
        }
        return;
      }

      // Handle legacy event format
      if (!event || !payload) return;

      // Handle SAB-specific events from backend
      if (event.startsWith("sab.")) {
        const eventType = event.split(".")[1]; // dashboard, events, mainboard, scrollboard
        const eventAction = event.split(".")[2]; // created, updated, deleted
        
        playNotificationSound();
        
        notification.info({
          message: `${eventType.charAt(0).toUpperCase() + eventType.slice(1)} ${eventAction}`,
          description: payload.title || `GTSA ${eventType} has been ${eventAction}`,
          duration: 4,
          placement: 'topRight',
        });
        return;
      }

      // Handle other notification events (generic)
      if (event === "booking.created" || event === "booking.updated") {
        if (!payload.url) return;
        playNotificationSound();
        addFromWs({ ...payload, event });

        if (Notification?.permission === "granted") {
          new Notification(payload.title || "Golden Triangle Sikh Association", {
            body: payload.body || "",
          });
        }
      }
    }

    function start() {
      const token = getToken();
      if (!token) return false;

      const wsUrl = getWsUrl();
      console.log("[ws] starting singleton on", wsUrl);

      ensureWS(wsUrl, token);

      const unsub = subscribe(handleMessage);
      return unsub;
    }

    // Try start immediately
    let cleanup = start();
    cleanupRef.current = cleanup || null;

    // If token not ready yet, poll for it for a bit
    if (!cleanup) {
      tokenPollRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const maybeCleanup = start();
        if (maybeCleanup) {
          cleanupRef.current = maybeCleanup;
          clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 1000);
    }

    return () => {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [addFromWs]);

  return null;
}
